import React, { useEffect, useState } from 'react';
import { FaSearch, FaBox, FaTimes } from 'react-icons/fa';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    MenuItem,
    Grid,
    Typography,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Paper,
    Chip,
    Checkbox,
    ListItemIcon,
} from '@mui/material';
import { getData } from '../../Webservice/Webservice';

const defaultFormValues = {
    title: '',
    layoutId: '',
    position: '',
    product_ids: [],
    status: 'active',
};

const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
];

const LayoutForm = ({ open, handleClose, handleSubmit, isEdit = false, editingLayout = null, inline = false }) => {
    const [formValues, setFormValues] = useState(defaultFormValues);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [showProductList, setShowProductList] = useState(false);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState(['All']);
    const [layoutOptions, setLayoutOptions] = useState([]);

    useEffect(() => {
        if (isEdit && editingLayout) {
            setFormValues({
                title: editingLayout.type || '',
                layoutId: editingLayout.layoutId || '',
                position: editingLayout.position?.toString() || '',
                product_ids: Array.isArray(editingLayout.product_ids)
                    ? editingLayout.product_ids
                    : (Array.isArray(editingLayout.products) ? editingLayout.products.map(product => product._id || product.id || product).filter(Boolean) : []),
                status: editingLayout.status || 'active',
            });
        } else {
            setFormValues(defaultFormValues);
        }
    }, [isEdit, editingLayout, open]);

    const onChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const onProductChange = (selectedIds) => {
        setFormValues((prev) => ({ ...prev, product_ids: selectedIds }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        console.log('Submitting form with values:', formValues); // Debug log
        if (handleSubmit) {
            handleSubmit(formValues);
        }
    };

    const fetchCategories = async () => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('categories/list?limit=1000', null, token);

            if (response && Array.isArray(response.categories)) {
                setCategories(response.categories);
                const categoryTitles = response.categories.map(cat => cat.title);
                setCategoryOptions(['All', ...categoryTitles]);
            } else {
                console.error('Invalid categories response:', response);
                setCategoryOptions(['All']);
            }
        } catch (error) {
            console.error('Fetch categories failed:', error);
            setCategoryOptions(['All']);
        }
    };

    const fetchProducts = async () => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('product/list?isFree=false&limit=1000', null, token);

            const transformedProducts = Array.isArray(response.products)
                ? response.products.map(product => ({
                    id: product._id,
                    name: product.title,
                    category: product.category_id?.title || 'N/A',
                }))
                : [];

            setProducts(transformedProducts);
        } catch (error) {
            console.error('Fetch products failed:', error);
            setProducts([]);
        }
    };

    const fetchLayoutIds = async () => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('homepage/layout/master/list', null, token);

            if (response && Array.isArray(response.layout)) {
                setLayoutOptions(response.layout.map(layout => ({
                    id: layout._id,
                    name: layout.name,
                })));
            } else {
                console.error('Invalid layout response:', response);
                setLayoutOptions([]);
            }
        } catch (error) {
            console.error('Fetch layout IDs failed:', error);
            setLayoutOptions([]);
        }
    };

    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.id?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'All' ||
            product.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const handleToggleProduct = (productId) => {
        const currentIndex = formValues.product_ids.indexOf(productId);
        const newChecked = [...formValues.product_ids];

        if (currentIndex === -1) {
            newChecked.push(productId);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        onProductChange(newChecked);
    };

    const handleDeleteChip = (productIdToDelete) => {
        const newProductIds = formValues.product_ids.filter(id => id !== productIdToDelete);
        onProductChange(newProductIds);
    };

    useEffect(() => {
        if (open) {
            fetchCategories();
            fetchProducts();
            fetchLayoutIds();
            setShowProductList(true);
        }
    }, [open]);

    const formContent = (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: inline ? 0 : 2.5,
                    px: inline ? 0 : 3,
                    backgroundColor: inline ? 'transparent' : '#fff',
                }}
            >
                <DialogTitle
                    sx={{
                        fontFamily: 'Mulish, sans-serif',
                        fontWeight: 800,
                        fontSize: '1.5rem',
                        py: inline ? 0 : 2.5,
                        px: inline ? 0 : 3,
                        color: 'black',
                    }}
                >
                    {isEdit ? 'Edit Layout' : 'Add Layout'}
                </DialogTitle>
                <Button
                    onClick={handleClose}
                    sx={{
                        color: "#white",
                        backgroundColor: '#ef4444',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2,
                        '&:hover': {
                            borderColor: '#ef4444',
                            backgroundColor: '#ef4444'
                        }
                    }}
                    variant="contained"
                >
                    Back
                </Button>
            </Box>
            <Box component="form" onSubmit={onSubmit}>
                <DialogContent >
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                            <TextField
                                label="Title"
                                name="title"
                                value={formValues.title}
                                onChange={onChange}
                                fullWidth
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Position"
                                name="position"
                                type="number"
                                value={formValues.position}
                                onChange={onChange}
                                fullWidth
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6}>
                            <TextField
                                select
                                label="Layout ID"
                                name="layoutId"
                                value={formValues.layoutId}
                                onChange={onChange}
                                fullWidth
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                    }
                                }}
                            >
                                {layoutOptions.length > 0 ? (
                                    layoutOptions.map((layout) => (
                                        <MenuItem key={layout.id} value={layout.id}>
                                            {layout.name}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem value="" disabled>
                                        No layout IDs available
                                    </MenuItem>
                                )}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                select
                                label="Status"
                                name="status"
                                value={formValues.status}
                                onChange={onChange}
                                fullWidth
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                    }
                                }}
                            >
                                {statusOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>

                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 600,
                            mb: 2,
                            color: '#334155',
                            display: 'block',
                        }}
                    >
                        Product Information
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                            <TextField
                                select
                                label="Filter by Category"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                fullWidth
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                    }
                                }}
                            >
                                {categoryOptions.map((category) => (
                                    <MenuItem key={category} value={category}>
                                        {category}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onFocus={() => setShowProductList(true)}
                                fullWidth
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FaSearch style={{ color: '#64748b' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>

                    {formValues.product_ids.length > 0 && (
                        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {formValues.product_ids.map(productId => {
                                const product = products.find(p => p.id === productId) ||
                                    editingLayout?.products?.find(p => (p._id || p.id) === productId);
                                return (
                                    <Chip
                                        key={productId}
                                        label={product ? `${product.name || product.title}` : `ID: ${productId}`}
                                        onDelete={() => handleDeleteChip(productId)}
                                        deleteIcon={<FaTimes />}
                                        variant="outlined"
                                        sx={{
                                            backgroundColor: '#f0f4f8',
                                            borderRadius: '6px',
                                            '& .MuiChip-deleteIcon': {
                                                color: '#64748b',
                                                fontSize: '14px',
                                                '&:hover': {
                                                    color: '#ef4444'
                                                }
                                            }
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    )}

                    {showProductList && (
                        <Paper
                            elevation={2}
                            sx={{
                                maxHeight: 200,
                                overflow: 'auto',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                mt: 1,
                            }}
                        >
                            <List dense>
                                {products.length === 0 ? (
                                    <ListItem>
                                        <ListItemText primary="Loading products..." />
                                    </ListItem>
                                ) : filteredProducts.length > 0 ? (
                                    filteredProducts.map((product) => (
                                        <ListItem
                                            key={product.id}
                                            button
                                            onClick={() => handleToggleProduct(product.id)}
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: '#f1f5f9',
                                                }
                                            }}
                                        >
                                            <ListItemIcon>
                                                <Checkbox
                                                    edge="start"
                                                    checked={formValues.product_ids.indexOf(product.id) !== -1}
                                                    tabIndex={-1}
                                                    disableRipple
                                                    sx={{
                                                        color: 'rgb(255 209 2)',
                                                        '&.Mui-checked': {
                                                            color: 'rgb(255 209 2)',
                                                        },
                                                    }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={product.name}
                                                secondary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                                        <FaBox style={{ fontSize: 14, marginRight: 4, color: '#64748b' }} />
                                                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                            {product.category}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))
                                ) : (
                                    <ListItem>
                                        <ListItemText primary="No products found" />
                                    </ListItem>
                                )}
                            </List>
                        </Paper>
                    )}
                </DialogContent>

                <DialogActions sx={{ paddingTop: '25px', paddingRight: '20px' }}>
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{
                            background: 'rgb(255 209 2)',
                            color: 'black',
                            border: '1px solid black',
                            fontWeight: 600,
                            borderRadius: '8px',
                            textTransform: 'none',
                            // px: 2,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            '&:hover': {
                                background: 'rgb(255 209 2)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                            }
                        }}
                    >
                        {isEdit ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Box>
        </>
    );

    if (inline) {
        return (
            <Box sx={{ backgroundColor: '#fff', borderRadius: '12px', p: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
                {formContent}
            </Box>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                    overflow: 'hidden',
                },
            }}
        >
            {formContent}
        </Dialog>
    );
};

export default LayoutForm;